import { useState, useEffect, useCallback, useRef } from 'react';
import type { FormEvent } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronRight,
  Users,
  X,
} from 'lucide-react';
import {
  listResellers,
  createReseller,
  updateReseller,
  deleteReseller,
  getResellerQuotes,
  listResellerContacts,
  createResellerContact,
  deleteResellerContact,
  ApiError,
} from '../api/client.js';
import type { Reseller, ResellerQuote, ResellerContact } from '../api/client.js';
import { formatPhone } from '../utils/phone.js';
import { useEntityLabels } from '../hooks/useEntityLabels.js';

function isValidEmail(email: string): boolean {
  if (!email) return true;
  const atIdx = email.indexOf('@');
  return atIdx > 0 && email.indexOf('.', atIdx) > atIdx + 1;
}

function formatPercent(decimal: string | number): string {
  const num = typeof decimal === 'string' ? parseFloat(decimal) : decimal;
  return `${(num * 100).toFixed(0)}%`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-800 text-gray-400',
  ready: 'bg-blue-900/30 text-blue-400',
  sent: 'bg-amber-900/30 text-amber-400',
  archived: 'bg-purple-900/30 text-purple-400',
};

export function ResellersPage() {
  const labels = useEntityLabels();
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingReseller, setEditingReseller] = useState<Reseller | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalDiscount, setModalDiscount] = useState('');
  const [modalActive, setModalActive] = useState(true);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Delete state
  const [deletingReseller, setDeletingReseller] = useState<Reseller | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Expanded row state
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedQuotes, setExpandedQuotes] = useState<ResellerQuote[]>([]);
  const [expandedContacts, setExpandedContacts] = useState<ResellerContact[]>([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  // Add contact form
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [addingContact, setAddingContact] = useState(false);
  const [contactEmailError, setContactEmailError] = useState('');

  const loadResellers = useCallback(
    async (search?: string) => {
      try {
        const data = await listResellers({
          active: activeFilter === 'all' ? undefined : activeFilter === 'active',
          search: search || undefined,
        });
        setResellers(data.resellers);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load resellers');
      } finally {
        setLoading(false);
      }
    },
    [activeFilter],
  );

  useEffect(() => {
    setLoading(true);
    loadResellers(searchTerm);
  }, [loadResellers, searchTerm]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadResellers(searchTerm);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, loadResellers]);

  const openCreate = () => {
    setEditingReseller(null);
    setModalName('');
    setModalDiscount('0');
    setModalActive(true);
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (reseller: Reseller) => {
    setEditingReseller(reseller);
    setModalName(reseller.name);
    setModalDiscount(String(parseFloat(reseller.defaultDiscount) * 100));
    setModalActive(reseller.active);
    setModalError('');
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setModalSaving(true);
    setModalError('');

    const discountDecimal = labels.isReseller ? 0 : (() => {
      const discountPct = parseFloat(modalDiscount);
      if (isNaN(discountPct) || discountPct < 0 || discountPct > 100) {
        setModalError('Discount must be between 0 and 100');
        setModalSaving(false);
        return null;
      }
      return discountPct / 100;
    })();
    if (discountDecimal === null) return;

    try {
      if (editingReseller) {
        await updateReseller(editingReseller.id, {
          name: modalName,
          defaultDiscount: discountDecimal,
          active: modalActive,
        });
      } else {
        await createReseller({
          name: modalName,
          defaultDiscount: discountDecimal,
        });
      }
      setShowModal(false);
      await loadResellers(searchTerm);
    } catch (err) {
      setModalError(err instanceof ApiError ? err.message : 'Failed to save reseller');
    } finally {
      setModalSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingReseller) return;
    setDeleteLoading(true);
    setDeleteError('');

    try {
      await deleteReseller(deletingReseller.id);
      setDeletingReseller(null);
      await loadResellers(searchTerm);
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      } else {
        setDeleteError('Failed to delete reseller');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = async (reseller: Reseller) => {
    // Optimistic update
    setResellers((prev) =>
      prev.map((r) => (r.id === reseller.id ? { ...r, active: !r.active } : r)),
    );

    try {
      await updateReseller(reseller.id, { active: !reseller.active });
    } catch {
      // Revert on error
      setResellers((prev) =>
        prev.map((r) => (r.id === reseller.id ? { ...r, active: reseller.active } : r)),
      );
      setError('Failed to update reseller status');
    }
  };

  const toggleExpand = async (resellerId: string) => {
    if (expandedId === resellerId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(resellerId);
    setExpandedLoading(true);
    setExpandedQuotes([]);
    setExpandedContacts([]);
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setContactEmailError('');

    try {
      const [quotesData, contactsData] = await Promise.all([
        getResellerQuotes(resellerId, { limit: 10 }),
        listResellerContacts(resellerId).catch(() => ({ contacts: [] as ResellerContact[] })),
      ]);
      setExpandedQuotes(quotesData.quotes);
      setExpandedContacts(contactsData.contacts);
    } catch {
      setExpandedQuotes([]);
      setExpandedContacts([]);
    } finally {
      setExpandedLoading(false);
    }
  };

  const handleAddContact = async (resellerId: string) => {
    if (!contactName.trim()) return;
    setAddingContact(true);
    try {
      const data = await createResellerContact(resellerId, {
        name: contactName.trim(),
        email: contactEmail.trim() || undefined,
        phone: contactPhone.trim() || undefined,
      });
      setExpandedContacts((prev) => [...prev, data.contact]);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  const handleDeleteContact = async (resellerId: string, contactId: string) => {
    try {
      await deleteResellerContact(resellerId, contactId);
      setExpandedContacts((prev) => prev.filter((c) => c.id !== contactId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete contact');
    }
  };

  if (loading && resellers.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  const inputClasses =
    'w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">{labels.Resellers}</h1>
          <p className="mt-1 text-sm text-gray-400">Manage your {labels.reseller} partners</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          New {labels.Reseller}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search ${labels.resellers}...`}
            className="rounded-lg border border-gray-700 bg-gray-800 py-2 pl-9 pr-4 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
          className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="active">Active Only</option>
          <option value="all">All</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      {/* Resellers Table */}
      {resellers.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-4 text-lg font-medium text-gray-300">No {labels.resellers} yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Create your first {labels.reseller} to get started.
          </p>
          <button
            onClick={openCreate}
            className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Create {labels.Reseller}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800/50 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="w-8 px-4 py-3" />
                <th className="px-4 py-3">Name</th>
                {!labels.isReseller && <th className="px-4 py-3 text-right">Default Discount</th>}
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Quotes</th>
                <th className="px-4 py-3">Last Quote</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {resellers.map((reseller) => (
                <>
                  <tr
                    key={reseller.id}
                    className="border-b border-gray-800 hover:bg-gray-800/30"
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleExpand(reseller.id)}
                        className="text-gray-500 hover:text-gray-300"
                      >
                        {expandedId === reseller.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleExpand(reseller.id)}
                        className="text-sm font-medium text-gray-100 hover:text-brand-400"
                      >
                        {reseller.name}
                      </button>
                    </td>
                    {!labels.isReseller && (
                      <td className="px-4 py-3 text-right text-sm text-gray-100">
                        {formatPercent(reseller.defaultDiscount)}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          reseller.active
                            ? 'bg-green-900/30 text-green-400'
                            : 'bg-gray-800 text-gray-500'
                        }`}
                      >
                        {reseller.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-400">
                      {reseller.quoteCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {formatDate(reseller.lastQuoteDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {formatDate(reseller.dateAdded)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(reseller)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(reseller)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                          title={reseller.active ? 'Deactivate' : 'Activate'}
                        >
                          {reseller.active ? (
                            <ToggleRight className="h-4 w-4 text-green-400" />
                          ) : (
                            <ToggleLeft className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setDeleteError('');
                            setDeletingReseller(reseller);
                          }}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Detail Row */}
                  {expandedId === reseller.id && (
                    <tr key={`${reseller.id}-detail`} className="border-b border-gray-800">
                      <td colSpan={labels.isReseller ? 7 : 8} className="bg-gray-900/50 px-8 py-4">
                        <div className="mb-3 flex items-center gap-4 text-sm">
                          {!labels.isReseller && (
                            <span className="text-gray-400">
                              Discount: <span className="text-gray-200">{formatPercent(reseller.defaultDiscount)}</span>
                            </span>
                          )}
                          <span className="text-gray-400">
                            Added: <span className="text-gray-200">{formatDate(reseller.dateAdded)}</span>
                          </span>
                        </div>

                        {expandedLoading ? (
                          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
                            Loading...
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            {/* Contacts Section */}
                            <div>
                              <h4 className="mb-2 text-sm font-medium text-gray-300">Contacts</h4>
                              {expandedContacts.length === 0 ? (
                                <p className="mb-3 text-sm text-gray-500">No contacts yet.</p>
                              ) : (
                                <div className="mb-3 space-y-1">
                                  {expandedContacts.map((contact) => (
                                    <div
                                      key={contact.id}
                                      className="flex items-center justify-between rounded px-3 py-1.5 text-sm hover:bg-gray-800/30"
                                    >
                                      <div>
                                        <span className="text-gray-200">{contact.name}</span>
                                        {(contact.email || contact.phone) && (
                                          <span className="ml-2 text-xs text-gray-500">
                                            {[contact.email, contact.phone ? formatPhone(contact.phone) : null].filter(Boolean).join(' \u2022 ')}
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => handleDeleteContact(reseller.id, contact.id)}
                                        className="ml-2 rounded p-1 text-gray-600 hover:text-red-400"
                                        title="Remove contact"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Add contact form */}
                              <div className="flex flex-wrap items-end gap-2">
                                <input
                                  type="text"
                                  value={contactName}
                                  onChange={(e) => setContactName(e.target.value)}
                                  placeholder="Name"
                                  className="w-32 rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                                />
                                <div>
                                  <input
                                    type="email"
                                    value={contactEmail}
                                    onChange={(e) => {
                                      setContactEmail(e.target.value);
                                      if (contactEmailError) setContactEmailError('');
                                    }}
                                    onBlur={() => {
                                      if (contactEmail && !isValidEmail(contactEmail)) {
                                        setContactEmailError('Invalid email');
                                      } else {
                                        setContactEmailError('');
                                      }
                                    }}
                                    placeholder="Email"
                                    className={`w-40 rounded border bg-gray-800 px-2 py-1.5 text-xs text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none ${
                                      contactEmailError ? 'border-red-500' : 'border-gray-700'
                                    }`}
                                  />
                                  {contactEmailError && (
                                    <p className="mt-0.5 text-xs text-red-400">{contactEmailError}</p>
                                  )}
                                </div>
                                <input
                                  type="text"
                                  value={contactPhone}
                                  onChange={(e) => setContactPhone(e.target.value)}
                                  onBlur={() => {
                                    if (contactPhone) setContactPhone(formatPhone(contactPhone));
                                  }}
                                  placeholder="Phone"
                                  className="w-28 rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                                />
                                <button
                                  onClick={() => handleAddContact(reseller.id)}
                                  disabled={!contactName.trim() || addingContact}
                                  className="flex items-center gap-1 rounded bg-brand-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add
                                </button>
                              </div>
                            </div>

                            {/* Quotes Section */}
                            <div>
                              <h4 className="mb-2 text-sm font-medium text-gray-300">Recent Quotes</h4>
                              {expandedQuotes.length === 0 ? (
                                <p className="py-2 text-sm text-gray-500">
                                  No quotes for this {labels.reseller} yet.
                                </p>
                              ) : (
                                <>
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                                        <th className="px-3 py-2">Quote #</th>
                                        <th className="px-3 py-2">Project</th>
                                        <th className="px-3 py-2">Status</th>
                                        <th className="px-3 py-2">Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedQuotes.map((quote) => (
                                        <tr
                                          key={quote.id}
                                          className="border-t border-gray-800/50"
                                        >
                                          <td className="px-3 py-2 text-gray-300">
                                            {quote.displayId || '\u2014'}
                                          </td>
                                          <td className="px-3 py-2 text-gray-300">
                                            {quote.projectName || '\u2014'}
                                          </td>
                                          <td className="px-3 py-2">
                                            <span
                                              className={`rounded-full px-2 py-0.5 text-xs ${statusColors[quote.status] ?? 'bg-gray-800 text-gray-400'}`}
                                            >
                                              {quote.status}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-gray-400">
                                            {formatDate(quote.createdAt)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {expandedQuotes.length >= 10 && (
                                    <p className="mt-2 text-xs text-gray-500">
                                      Showing most recent 10
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-100">
              {editingReseller ? `Edit ${labels.Reseller}` : `New ${labels.Reseller}`}
            </h2>

            {modalError && (
              <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {modalError}
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label htmlFor="reseller-name" className="mb-1 block text-sm text-gray-400">
                  Name *
                </label>
                <input
                  id="reseller-name"
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  className={inputClasses}
                  placeholder="e.g. ABC Distribution"
                  required
                />
              </div>

              {!labels.isReseller && (
                <div>
                  <label htmlFor="reseller-discount" className="mb-1 block text-sm text-gray-400">
                    Default Discount
                  </label>
                  <div className="relative">
                    <input
                      id="reseller-discount"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={modalDiscount}
                      onChange={(e) => setModalDiscount(e.target.value)}
                      className={inputClasses}
                      placeholder="0"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                      %
                    </span>
                  </div>
                </div>
              )}

              {editingReseller && (
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={modalActive}
                    onChange={(e) => setModalActive(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-300">Active</span>
                </label>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalSaving}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {modalSaving
                    ? 'Saving...'
                    : editingReseller
                      ? 'Save Changes'
                      : `Create ${labels.Reseller}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingReseller && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Delete {labels.Reseller}</h2>

            {deleteError && (
              <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {deleteError}
              </div>
            )}

            <p className="mb-6 text-sm text-gray-400">
              Delete {labels.reseller} &ldquo;{deletingReseller.name}&rdquo;? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingReseller(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
